<!-- eslint-disable vue/valid-v-slot -->
<template>
  <div class="hr-page">
    <header class="hr-hero">
      <div class="hr-heading">
        <div class="hr-heading-icon" aria-hidden="true"><v-icon size="24">mdi-account-heart-outline</v-icon></div>
        <div>
          <span class="hr-eyebrow">Zespół i nieobecności</span>
          <h1>Kadry</h1>
          <p>Decyzje urlopowe, dostępność zespołu i pule dni w jednym widoku.</p>
        </div>
      </div>
      <div class="hr-hero-actions">
        <div class="hr-context-card">
          <span><v-icon size="18">mdi-account-group-outline</v-icon>{{ loadingOverview || overviewError ? '—' : people.length }} osób</span>
          <strong>{{ year }}</strong>
          <small>rok ewidencji</small>
        </div>
        <button type="button" class="hr-refresh" @click="otworzDzienWolny">
          <v-icon size="19">mdi-calendar-plus</v-icon>
          <span>Wpisz dzień wolny</span>
        </button>
        <button type="button" class="hr-refresh" :disabled="isLoading" @click="loadAll">
          <v-icon size="19" :class="{ 'is-spinning': isLoading }">mdi-refresh</v-icon>
          <span>{{ isLoading ? 'Odświeżam…' : 'Odśwież dane' }}</span>
        </button>
      </div>
    </header>

    <section class="hr-metrics" aria-label="Najważniejsze informacje kadrowe">
      <article class="hr-metric hr-metric--amber">
        <span class="hr-metric-icon"><v-icon size="21">mdi-inbox-arrow-down-outline</v-icon></span>
        <div><small>Do decyzji</small><strong>{{ loadingOverview || overviewError ? '—' : pending.length }}</strong><span>{{ overviewError ? 'brak aktualnych danych' : requestWaitingLabel(pending.length) }}</span></div>
      </article>
      <article class="hr-metric hr-metric--red">
        <span class="hr-metric-icon"><v-icon size="21">mdi-calendar-alert-outline</v-icon></span>
        <div><small>Kolizje terminów</small><strong>{{ loadingOverview || overviewError ? '—' : conflictingRequests.length }}</strong><span>{{ overviewError ? 'brak aktualnych danych' : conflictSummary }}</span></div>
      </article>
      <article class="hr-metric hr-metric--green">
        <span class="hr-metric-icon"><v-icon size="21">mdi-calendar-arrow-right</v-icon></span>
        <div><small>Najbliższe terminy</small><strong>{{ loadingOverview || overviewError ? '—' : upcoming.length }}</strong><span>{{ overviewError ? 'brak aktualnych danych' : 'zatwierdzonych nieobecności' }}</span></div>
      </article>
      <article class="hr-metric hr-metric--violet">
        <span class="hr-metric-icon"><v-icon size="21">mdi-counter</v-icon></span>
        <div><small>Pule do uwagi</small><strong>{{ loadingOverview || overviewError ? '—' : lowBalancePeople.length }}</strong><span>{{ overviewError ? 'brak aktualnych danych' : 'brak puli lub maks. 2 dni' }}</span></div>
      </article>
    </section>

    <!-- Wnioski do decyzji -->
    <section class="hr-panel hr-panel--requests">
      <header class="hr-section-head">
        <div class="hr-section-heading">
          <span class="hr-section-icon hr-section-icon--amber"><v-icon size="20">mdi-clipboard-clock-outline</v-icon></span>
          <div><span class="hr-section-eyebrow">Wymagają działania</span><h2>Wnioski do decyzji</h2><p>Najpierw sprawdź termin, wymiar oraz możliwe kolizje w zespole.</p></div>
        </div>
        <span class="hr-section-count" :class="{ 'has-items': pending.length }"><strong>{{ pending.length }}</strong>{{ requestNoun(pending.length) }}</span>
      </header>
      <v-progress-linear v-if="loadingOverview" indeterminate height="2" color="#FFBD66" class="hr-panel-progress" />

      <div v-if="overviewError" class="hr-inline-error">
        <span><v-icon size="20">mdi-alert-circle-outline</v-icon></span>
        <div><strong>Nie udało się pobrać danych kadrowych</strong><p>{{ overviewError }}</p></div>
        <button type="button" @click="loadOverview">Spróbuj ponownie</button>
      </div>
      <div v-else-if="!loadingOverview && pending.length === 0" class="hr-empty-state hr-empty-state--success">
        <span><v-icon size="31">mdi-check-decagram-outline</v-icon></span>
        <div><strong>Wszystkie wnioski są rozpatrzone</strong><p>Na ten moment nic nie czeka na Twoją decyzję.</p></div>
      </div>
      <div v-else class="hr-request-grid">
        <article v-for="r in pending" :key="r.id" class="hr-request-card" :class="{ 'has-conflict': r.conflicts && r.conflicts.length }">
          <span class="hr-request-accent" aria-hidden="true"></span>
          <header class="hr-request-top">
            <div class="hr-person">
              <span class="hr-avatar">{{ personInitials(r.admin) }}</span>
              <div><small>Wniosek pracownika</small><strong>{{ personName(r.admin) }}</strong></div>
            </div>
            <span class="hr-type-badge" :class="`is-${leaveTypeTone(r.type)}`"><v-icon size="16">{{ leaveTypeIcon(r.type) }}</v-icon>{{ typeLabel(r.type) }}</span>
          </header>

          <div class="hr-request-facts">
            <div><span><v-icon size="19">mdi-calendar-range-outline</v-icon></span><small>Termin</small><strong>{{ fmt(r.startDate) }} – {{ fmt(r.endDate) }}</strong></div>
            <div><span><v-icon size="19">mdi-timer-sand</v-icon></span><small>Wymiar</small><strong>{{ requestDuration(r) }}</strong></div>
          </div>

          <div v-if="r.note" class="hr-request-note"><v-icon size="18">mdi-note-text-outline</v-icon><p>{{ r.note }}</p></div>
          <div v-else class="hr-request-note is-empty"><v-icon size="18">mdi-note-off-outline</v-icon><p>Pracownik nie dodał uwag.</p></div>

          <div v-if="r.conflicts && r.conflicts.length" class="hr-conflict-box">
            <div class="hr-conflict-title"><v-icon size="20">mdi-calendar-alert-outline</v-icon><strong>Kolizja w terminie</strong><span>{{ r.conflicts.length }}</span></div>
            <div class="hr-conflict-list">
              <span v-for="(c, i) in r.conflicts" :key="i"><b>{{ c.name }}</b>{{ fmt(c.startDate) }}–{{ fmt(c.endDate) }}</span>
            </div>
          </div>

          <footer class="hr-request-actions">
            <v-btn outlined color="error" :loading="decidingId === r.id && decidingAction === 'REJECT'" :disabled="decidingId === r.id" @click="decide(r, 'REJECT')"><v-icon left size="19">mdi-close</v-icon>Odrzuć</v-btn>
            <v-btn color="success" :loading="decidingId === r.id && decidingAction === 'APPROVE'" :disabled="decidingId === r.id" @click="decide(r, 'APPROVE')"><v-icon left size="19">mdi-check</v-icon>Zatwierdź</v-btn>
          </footer>
        </article>
      </div>
    </section>

    <!-- Kalendarz zespołu -->
    <section class="hr-panel hr-panel--calendar">
      <header class="hr-section-head hr-section-head--calendar">
        <div class="hr-section-heading">
          <span class="hr-section-icon hr-section-icon--blue"><v-icon size="20">mdi-calendar-month-outline</v-icon></span>
          <div><span class="hr-section-eyebrow">Dostępność zespołu</span><h2>Kalendarz nieobecności</h2><p>Rozkład urlopów, oczekujących wniosków, weekendów i świąt.</p></div>
        </div>
        <div class="hr-month-control">
          <button type="button" aria-label="Poprzedni miesiąc" @click="shiftMonth(-1)"><v-icon size="20">mdi-chevron-left</v-icon></button>
          <span><small>Wybrany miesiąc</small><strong>{{ monthLabel }}</strong></span>
          <button type="button" aria-label="Następny miesiąc" @click="shiftMonth(1)"><v-icon size="20">mdi-chevron-right</v-icon></button>
        </div>
      </header>
      <v-progress-linear v-if="loadingCalendar" indeterminate height="2" color="#6688FF" class="hr-panel-progress" />

      <div v-if="calendarError" class="hr-inline-error">
        <span><v-icon size="20">mdi-calendar-remove-outline</v-icon></span>
        <div><strong>Nie udało się pobrać kalendarza</strong><p>{{ calendarError }}</p></div>
        <button type="button" @click="loadCalendar">Spróbuj ponownie</button>
      </div>
      <div v-else class="hr-calendar-layout">
        <div class="hr-calendar-main">
          <div class="hr-calendar-scroll hr-calendar-desktop">
            <table class="hr-cal">
              <thead>
                <tr>
                  <th class="hr-cal-name"><span>Zespół</span><small>{{ people.length }} osób</small></th>
                  <th
                    v-for="d in daysInMonth" :key="'h'+d"
                    :class="{ wknd: isWeekend(d), hol: !!holidayAt(d), 'is-today': isToday(d) }"
                    :title="holidayAt(d) || ''"
                  ><small>{{ weekdayShort(d) }}</small><strong>{{ d }}</strong></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in people" :key="p.id">
                  <td class="hr-cal-name"><span class="hr-cal-avatar">{{ personInitials(p) }}</span><strong>{{ personName(p) }}</strong></td>
                  <td
                    v-for="d in daysInMonth" :key="p.id + '-' + d"
                    :class="[cellClass(p.id, d), { 'is-today': isToday(d) }]"
                    :title="cellTitle(p.id, d)"
                  ></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="hr-calendar-mobile">
            <article v-for="r in calendar" :key="'m'+r.id" class="hr-agenda-card">
              <span class="hr-avatar">{{ personInitials(r.admin) }}</span>
              <div class="hr-agenda-copy"><strong>{{ personName(r.admin) }}</strong><span>{{ fmt(r.startDate) }} – {{ fmt(r.endDate) }}</span><small>{{ typeLabel(r.type) }} · {{ requestDuration(r) }}</small></div>
              <span class="hr-status-badge" :class="r.status === 'APPROVED' ? 'is-approved' : 'is-pending'">{{ statusLabel(r.status) }}</span>
            </article>
            <div v-if="!loadingCalendar && !calendar.length" class="hr-empty-state"><span><v-icon size="27">mdi-calendar-check-outline</v-icon></span><div><strong>Brak nieobecności</strong><p>W tym miesiącu kalendarz zespołu jest pusty.</p></div></div>
          </div>

          <div class="hr-legend" aria-label="Legenda kalendarza">
            <span><i class="is-approved"></i>zatwierdzony</span>
            <span><i class="is-pending"></i>oczekujący</span>
            <span><i class="is-weekend"></i>weekend</span>
            <span><i class="is-holiday"></i>święto</span>
          </div>
        </div>

        <aside class="hr-upcoming">
          <header><span><v-icon size="20">mdi-calendar-arrow-right</v-icon></span><div><small>Następne terminy</small><strong>Nadchodzące nieobecności</strong></div><b>{{ upcoming.length }}</b></header>
          <div v-if="upcoming.length" class="hr-upcoming-list">
            <article v-for="r in upcoming" :key="'u'+r.id">
              <span class="hr-upcoming-marker"></span>
              <div class="hr-upcoming-top"><strong>{{ personName(r.admin) }}</strong><small>{{ upcomingHeadline(r) }}</small></div>
              <p>{{ fmt(r.startDate) }} – {{ fmt(r.endDate) }}</p>
              <span>{{ typeLabel(r.type) }} · {{ requestDuration(r) }}</span>
            </article>
          </div>
          <div v-else class="hr-upcoming-empty"><v-icon size="28">mdi-calendar-blank-outline</v-icon><strong>Brak zaplanowanych nieobecności</strong><span>Najbliższy okres jest wolny.</span></div>
        </aside>
      </div>
    </section>

    <!-- Pule urlopowe -->
    <section class="hr-panel hr-panel--allowances">
      <header class="hr-section-head">
        <div class="hr-section-heading">
          <span class="hr-section-icon hr-section-icon--violet"><v-icon size="20">mdi-chart-donut</v-icon></span>
          <div><span class="hr-section-eyebrow">Administracja roczna</span><h2>Pule urlopowe · {{ year }}</h2><p>Kontroluj wykorzystanie i koryguj limit dni dla każdej osoby.</p></div>
        </div>
        <div class="hr-balance-summary"><small>Pozostało w zespole</small><strong>{{ fmtPool(teamRemainingHours) }}</strong></div>
      </header>
      <v-progress-linear v-if="loadingOverview" indeterminate height="2" color="#9B8CFF" class="hr-panel-progress" />

      <div v-if="overviewError" class="hr-inline-error">
        <span><v-icon size="20">mdi-database-alert-outline</v-icon></span>
        <div><strong>Nie udało się pobrać pul urlopowych</strong><p>{{ overviewError }}</p></div>
        <button type="button" @click="loadOverview">Spróbuj ponownie</button>
      </div>
      <div v-else class="hr-allowance-table hr-allowance-desktop">
        <div class="hr-allowance-head"><span>Pracownik</span><span>Wykorzystanie puli</span><span>Pozostało</span><span>Roczny limit</span></div>
        <article v-for="p in people" :key="'a'+p.id" class="hr-allowance-row" :class="`is-${poolTone(p)}`">
          <div class="hr-person"><span class="hr-avatar">{{ personInitials(p) }}</span><div><strong>{{ personName(p) }}</strong><small>{{ p.email }}</small></div></div>
          <div class="hr-pool-progress"><div><span>{{ fmtPool(p.usedHours) }} wykorzystano</span><b>{{ p.totalDays }} dni</b></div><span class="hr-progress-track"><i :style="{ width: poolProgress(p) + '%' }"></i></span></div>
          <div class="hr-remaining"><small>{{ poolStatusLabel(p) }}</small><strong>{{ fmtPool(p.remainingHours) }}</strong></div>
          <div class="hr-allowance-editor">
            <label><input v-model.number="edits[p.id]" type="number" min="0" max="365" :placeholder="String(p.totalDays)" /><span>dni</span></label>
            <v-btn small color="primary" :loading="savingId === p.id" :disabled="!allowanceChanged(p)" @click="saveAllowance(p)">Zapisz</v-btn>
          </div>
        </article>
        <div v-if="!loadingOverview && !people.length" class="hr-empty-state"><span><v-icon size="27">mdi-account-search-outline</v-icon></span><div><strong>Brak pracowników</strong><p>Nie znaleziono osób, dla których można ustawić pulę.</p></div></div>
      </div>

      <div v-if="!overviewError" class="hr-allowance-mobile">
        <article v-for="p in people" :key="'am'+p.id" class="hr-pool-card" :class="`is-${poolTone(p)}`">
          <header><div class="hr-person"><span class="hr-avatar">{{ personInitials(p) }}</span><div><strong>{{ personName(p) }}</strong><small>{{ p.email }}</small></div></div><span>{{ poolStatusLabel(p) }}</span></header>
          <div class="hr-pool-mobile-facts"><div><small>Pula</small><strong>{{ p.totalDays }} dni</strong></div><div><small>Wykorzystano</small><strong>{{ fmtPool(p.usedHours) }}</strong></div><div><small>Pozostało</small><strong>{{ fmtPool(p.remainingHours) }}</strong></div></div>
          <span class="hr-progress-track"><i :style="{ width: poolProgress(p) + '%' }"></i></span>
          <footer class="hr-allowance-editor"><label><input v-model.number="edits[p.id]" type="number" min="0" max="365" :placeholder="String(p.totalDays)" /><span>dni</span></label><v-btn small color="primary" :loading="savingId === p.id" :disabled="!allowanceChanged(p)" @click="saveAllowance(p)">Zapisz limit</v-btn></footer>
        </article>
      </div>
    </section>
  
    <!-- Dzień wolny wpisywany przez headadmina bez wniosku pracownika:
         dla wolnego ustalonego ustnie albo odnotowywanego wstecz. -->
    <v-dialog v-model="wolne.open" max-width="520" persistent>
      <v-card>
        <v-card-title class="text-h6">Wpisz dzień wolny</v-card-title>
        <v-card-subtitle>
          Wpis trafia od razu jako zatwierdzony, bez wniosku pracownika.
          W historii zostaje ślad, że zrobiło to biuro.
        </v-card-subtitle>
        <v-divider></v-divider>
        <v-card-text>
          <v-select
            v-model="wolne.adminId" :items="osobyDoWyboru" item-text="nazwa" item-value="id"
            label="Pracownik" dense outlined hide-details="auto" class="mb-3"></v-select>

          <v-select
            v-model="wolne.type" :items="typyWolnego" item-text="etykieta" item-value="wartosc"
            label="Rodzaj" dense outlined hide-details="auto" class="mb-3"></v-select>

          <div class="d-flex" style="gap: 12px;">
            <v-text-field
              v-model="wolne.startDate" type="date" dense outlined hide-details="auto"
              :label="wolne.type === 'HOURLY' ? 'Dzień' : 'Od'" class="mb-3"></v-text-field>
            <v-text-field
              v-if="wolne.type !== 'HOURLY'"
              v-model="wolne.endDate" type="date" dense outlined hide-details="auto"
              label="Do" class="mb-3"></v-text-field>
            <v-text-field
              v-else
              v-model.number="wolne.hours" type="number" min="1" max="7" dense outlined
              hide-details="auto" label="Godzin (1-7)" class="mb-3"></v-text-field>
          </div>

          <v-text-field
            v-model="wolne.note" label="Notatka (opcjonalnie)" dense outlined
            hide-details="auto" maxlength="500" class="mb-2"></v-text-field>

          <!-- Pokazujemy sie dopiero, gdy serwer odmowil z powodu puli —
               zeby przekroczenie bylo swiadome, a nie domyslne. -->
          <v-checkbox
            v-if="wolne.pulaZablokowala"
            v-model="wolne.pomimoBrakuPuli" dense hide-details
            label="Wpisz mimo braku dni w puli pracownika"></v-checkbox>

          <v-alert v-if="wolne.error" type="warning" dense outlined class="mt-3 mb-0">
            {{ wolne.error }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="wolne.saving" @click="wolne.open = false">Anuluj</v-btn>
          <v-btn color="primary" :loading="wolne.saving"
                 :disabled="!wolne.adminId || !wolne.startDate"
                 @click="zapiszDzienWolny">Wpisz</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import { leaveTypeLabel, leaveStatusLabel, fmtPoolHours } from '@/services/leaveDicts';
import { polishHolidays } from '@/services/polishHolidays';

export default {
  name: 'HrPage',
  data() {
    const now = new Date();
    return {
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      people: [],
      pending: [],
      upcoming: [],
      calendar: [],
      edits: {},
      decidingId: null,
      decidingAction: null,
      savingId: null,
      loadingOverview: false,
      loadingCalendar: false,
      overviewError: '',
      calendarError: '',
      wolne: {
        open: false, adminId: '', type: 'SPECIAL',
        startDate: '', endDate: '', hours: 4, note: '',
        pomimoBrakuPuli: false, pulaZablokowala: false,
        saving: false, error: '',
      },
    };
  },
  computed: {
    daysInMonth() {
      return new Date(this.year, this.month, 0).getDate();
    },
    monthLabel() {
      return new Date(this.year, this.month - 1, 1).toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' });
    },
    isLoading() {
      return this.loadingOverview || this.loadingCalendar;
    },
    /** Pracownicy do wyboru przy wpisywaniu wolnego — te same osoby co w puli. */
    osobyDoWyboru() {
      return (this.people || []).map((p) => ({
        id: p.id,
        nazwa: `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.email || '—',
      }));
    },
    typyWolnego() {
      // Kolejnosc od najczestszych przy wpisie recznym: biuro odnotowuje
      // zwykle wolne ustalone poza pula, rzadziej urlop z puli.
      return [
        { wartosc: 'SPECIAL', etykieta: this.typeLabel('SPECIAL') },
        { wartosc: 'UNPAID', etykieta: this.typeLabel('UNPAID') },
        { wartosc: 'VACATION', etykieta: this.typeLabel('VACATION') },
        { wartosc: 'ON_DEMAND', etykieta: this.typeLabel('ON_DEMAND') },
        { wartosc: 'HOURLY', etykieta: this.typeLabel('HOURLY') },
        { wartosc: 'OTHER', etykieta: this.typeLabel('OTHER') },
      ];
    },
    conflictingRequests() {
      return this.pending.filter((r) => Array.isArray(r.conflicts) && r.conflicts.length > 0);
    },
    conflictSummary() {
      if (!this.conflictingRequests.length) return 'brak nakładających się terminów';
      const noun = this.requestNoun(this.conflictingRequests.length);
      return `${noun} ${noun === 'wniosek' ? 'wymaga' : 'wymagają'} uwagi`;
    },
    lowBalancePeople() {
      return this.people.filter((p) => Number(p.totalDays || 0) <= 0 || Number(p.remainingHours || 0) <= 16);
    },
    teamRemainingHours() {
      return this.people.reduce((sum, p) => sum + Number(p.remainingHours || 0), 0);
    },
  },
  async created() {
    await this.loadAll();
  },
  methods: {
    getConfig() {
      return { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } };
    },
    async loadAll() {
      await Promise.all([this.loadOverview(), this.loadCalendar()]);
    },
    async loadOverview() {
      this.loadingOverview = true;
      this.overviewError = '';
      try {
        const res = await axios.get(process.env.VUE_APP_ROOT_API + 'hr/admin/overview?year=' + this.year, this.getConfig());
        const data = res.data?.data || {};
        this.people = data.people || [];
        this.pending = data.pending || [];
        this.upcoming = data.upcoming || [];
      } catch (e) {
        console.error(e);
        this.overviewError = e.response?.data?.message || 'Sprawdź połączenie i spróbuj ponownie.';
      } finally {
        this.loadingOverview = false;
      }
    },
    async loadCalendar() {
      this.loadingCalendar = true;
      this.calendarError = '';
      try {
        const res = await axios.get(
          process.env.VUE_APP_ROOT_API + `hr/admin/calendar?year=${this.year}&month=${this.month}`,
          this.getConfig(),
        );
        this.calendar = res.data?.data || [];
      } catch (e) {
        console.error(e);
        this.calendarError = e.response?.data?.message || 'Sprawdź połączenie i spróbuj ponownie.';
      } finally {
        this.loadingCalendar = false;
      }
    },
    async shiftMonth(delta) {
      let m = this.month + delta;
      let y = this.year;
      if (m < 1) { m = 12; y--; }
      if (m > 12) { m = 1; y++; }
      this.month = m;
      if (y !== this.year) { this.year = y; await this.loadOverview(); }
      await this.loadCalendar();
    },
    dayDate(d) {
      return new Date(Date.UTC(this.year, this.month - 1, d));
    },
    isWeekend(d) {
      const dow = this.dayDate(d).getUTCDay();
      return dow === 0 || dow === 6;
    },
    holidayAt(d) {
      return polishHolidays(this.year)[this.dayDate(d).toISOString().slice(0, 10)];
    },
    leaveAt(adminId, d) {
      const date = this.dayDate(d).toISOString().slice(0, 10);
      return this.calendar.find(
        (r) => r.adminId === adminId && this.dateKey(r.startDate) <= date && date <= this.dateKey(r.endDate),
      );
    },
    cellClass(adminId, d) {
      const r = this.leaveAt(adminId, d);
      return {
        hol: !!this.holidayAt(d),
        wknd: this.isWeekend(d),
        appr: r?.status === 'APPROVED',
        pend: r?.status === 'PENDING',
      };
    },
    cellTitle(adminId, d) {
      const h = this.holidayAt(d);
      const r = this.leaveAt(adminId, d);
      return [h, r ? `${this.typeLabel(r.type)} (${leaveStatusLabel(r.status)})` : ''].filter(Boolean).join(' · ');
    },
    async decide(r, action) {
      let note;
      if (action === 'REJECT') {
        const res = await Swal.fire({
          title: 'Odrzucić wniosek?',
          input: 'text',
          inputLabel: 'Powód (opcjonalnie)',
          showCancelButton: true,
          confirmButtonText: 'Odrzuć',
          cancelButtonText: 'Wróć',
          confirmButtonColor: '#E75D6C',
          cancelButtonColor: '#30384B',
          background: '#1D2432',
          color: '#F5F7FC',
          reverseButtons: true,
          focusCancel: true,
          customClass: {
            popup: 'hr-swal',
            input: 'hr-swal-input',
            confirmButton: 'hr-swal-confirm',
            cancelButton: 'hr-swal-cancel',
          },
        });
        if (!res.isConfirmed) return;
        note = res.value || undefined;
      }
      this.decidingId = r.id;
      this.decidingAction = action;
      try {
        await axios.patch(
          process.env.VUE_APP_ROOT_API + 'hr/admin/requests/' + r.id,
          { action, note },
          this.getConfig(),
        );
        await this.loadAll();
      } catch (e) {
        this.showHrError(e.response?.data?.message || 'Nie udało się zapisać decyzji.');
      } finally {
        this.decidingId = null;
        this.decidingAction = null;
      }
    },
    async saveAllowance(p) {
      const val = this.edits[p.id];
      if (val === undefined || val === null || val === '') return;
      this.savingId = p.id;
      try {
        await axios.put(
          process.env.VUE_APP_ROOT_API + 'hr/admin/allowance',
          { adminId: p.id, year: this.year, totalDays: val },
          this.getConfig(),
        );
        this.$delete(this.edits, p.id);
        await this.loadOverview();
      } catch (e) {
        this.showHrError(e.response?.data?.message || 'Nie udało się zapisać puli.');
      } finally {
        this.savingId = null;
      }
    },
    personName(p) {
      if (!p) return '—';
      return `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.email || '—';
    },
    personInitials(p) {
      if (!p) return '—';
      const first = String(p.firstName || '').trim().charAt(0);
      const last = String(p.lastName || '').trim().charAt(0);
      return (first + last).toUpperCase() || String(p.email || '?').charAt(0).toUpperCase();
    },
    requestNoun(count) {
      const value = Math.abs(Number(count || 0));
      if (value === 1) return 'wniosek';
      if (value % 10 >= 2 && value % 10 <= 4 && (value % 100 < 12 || value % 100 > 14)) return 'wnioski';
      return 'wniosków';
    },
    requestWaitingLabel(count) {
      const noun = this.requestNoun(count);
      return noun === 'wnioski' ? `${noun} oczekują` : `${noun} oczekuje`;
    },
    requestDuration(r) {
      if (r.type === 'HOURLY') {
        const hours = Number(r.hours || 0);
        const suffix = hours === 1 ? 'godzina' : (hours >= 2 && hours <= 4 ? 'godziny' : 'godzin');
        return `${hours} ${suffix}`;
      }
      const days = Number(r.days || 0);
      return days === 1 ? '1 dzień' : `${days} dni`;
    },
    leaveTypeIcon(type) {
      return {
        VACATION: 'mdi-beach',
        ON_DEMAND: 'mdi-lightning-bolt-outline',
        HOURLY: 'mdi-clock-outline',
        SPECIAL: 'mdi-party-popper',
        UNPAID: 'mdi-calendar-remove-outline',
        OTHER: 'mdi-dots-horizontal-circle-outline',
      }[type] || 'mdi-calendar-outline';
    },
    leaveTypeTone(type) {
      return {
        VACATION: 'blue',
        ON_DEMAND: 'amber',
        HOURLY: 'violet',
        SPECIAL: 'green',
        UNPAID: 'neutral',
        OTHER: 'neutral',
      }[type] || 'neutral';
    },
    upcomingHeadline(r) {
      const today = this.warsawTodayKey();
      const start = this.dateKey(r.startDate);
      const end = this.dateKey(r.endDate);
      if (start <= today && today <= end) return 'Trwa teraz';
      const diff = this.daysBetween(today, start);
      if (diff === 0) return 'Dzisiaj';
      if (diff === 1) return 'Jutro';
      return diff > 1 ? `Za ${diff} dni` : 'W toku';
    },
    poolProgress(p) {
      const total = Number(p.totalHours || Number(p.totalDays || 0) * 8);
      if (total <= 0) return 0;
      return Math.max(0, Math.min(100, Math.round((Number(p.usedHours || 0) / total) * 100)));
    },
    poolTone(p) {
      const remaining = Number(p.remainingHours || 0);
      if (remaining < 0) return 'danger';
      if (Number(p.totalDays || 0) <= 0) return 'empty';
      if (remaining <= 16) return 'warning';
      return 'good';
    },
    poolStatusLabel(p) {
      const tone = this.poolTone(p);
      if (tone === 'danger') return 'Przekroczona pula';
      if (tone === 'empty') return 'Brak ustawionej puli';
      if (tone === 'warning') return 'Niska pula';
      return 'W normie';
    },
    allowanceChanged(p) {
      const value = this.edits[p.id];
      return value !== undefined && value !== null && value !== '' && !Number.isNaN(Number(value)) && Number(value) !== Number(p.totalDays);
    },
    weekdayShort(d) {
      return this.dayDate(d).toLocaleDateString('pl-PL', { weekday: 'narrow', timeZone: 'UTC' }).toUpperCase();
    },
    isToday(d) {
      return this.dayDate(d).toISOString().slice(0, 10) === this.warsawTodayKey();
    },
    dateKey(value) {
      const date = new Date(value);
      return Number.isNaN(+date) ? '' : date.toISOString().slice(0, 10);
    },
    warsawTodayKey() {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Warsaw', year: 'numeric', month: '2-digit', day: '2-digit',
      }).formatToParts(new Date());
      const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
      return `${values.year}-${values.month}-${values.day}`;
    },
    daysBetween(from, to) {
      const fromDate = new Date(`${from}T00:00:00Z`);
      const toDate = new Date(`${to}T00:00:00Z`);
      return Math.round((toDate - fromDate) / 86400000);
    },
    showHrError(message) {
      return Swal.fire({
        icon: 'error',
        title: 'Nie udało się zapisać zmian',
        text: message,
        confirmButtonText: 'Rozumiem',
        confirmButtonColor: '#6688FF',
        background: '#1D2432',
        color: '#F5F7FC',
        customClass: { popup: 'hr-swal', confirmButton: 'hr-swal-confirm' },
      });
    },
    fmt(d) { return new Date(d).toLocaleDateString('pl-PL', { timeZone: 'Europe/Warsaw' }); },
    fmtPool(totalHours) {
      const hours = Number(totalHours || 0);
      return hours < 0 ? `−${fmtPoolHours(Math.abs(hours))}` : fmtPoolHours(hours);
    },
    otworzDzienWolny() {
      const dzis = new Date().toISOString().slice(0, 10);
      this.wolne = {
        open: true, adminId: '', type: 'SPECIAL',
        startDate: dzis, endDate: dzis, hours: 4, note: '',
        pomimoBrakuPuli: false, pulaZablokowala: false,
        saving: false, error: '',
      };
    },
    async zapiszDzienWolny() {
      this.wolne.saving = true;
      this.wolne.error = '';
      const dane = {
        adminId: this.wolne.adminId,
        type: this.wolne.type,
        startDate: this.wolne.startDate,
        note: this.wolne.note || undefined,
        pomimoBrakuPuli: this.wolne.pomimoBrakuPuli || undefined,
      };
      if (this.wolne.type === 'HOURLY') dane.hours = Number(this.wolne.hours) || 0;
      else dane.endDate = this.wolne.endDate || this.wolne.startDate;

      try {
        await axios.post(
          process.env.VUE_APP_ROOT_API + 'hr/admin/day-off',
          dane,
          this.getConfig(),
        );
        this.wolne.open = false;
        await this.loadAll();
      } catch (e) {
        const tekst = (e.response && e.response.data && e.response.data.message) || 'Nie udało się wpisać';
        this.wolne.error = Array.isArray(tekst) ? tekst.join(', ') : tekst;
        // Pula to jedyny przypadek, ktory wolno swiadomie przepchnac — dopiero
        // wtedy pokazujemy pole potwierdzenia.
        if (this.wolne.error.includes('puli')) this.wolne.pulaZablokowala = true;
      } finally {
        this.wolne.saving = false;
      }
    },
    typeLabel: leaveTypeLabel,
    statusLabel: leaveStatusLabel,
  },
};
</script>

<style scoped>
.hr-page {
  --hr-bg: #181C28;
  --hr-surface: #232838;
  --hr-surface-soft: #1D2230;
  --hr-surface-hover: #292F42;
  --hr-border: rgba(255, 255, 255, 0.075);
  --hr-border-strong: rgba(126, 151, 255, 0.22);
  --hr-text: #F5F7FC;
  --hr-muted: #A5B0C3;
  --hr-subtle: #7F8AA0;
  --hr-blue: #6688FF;
  --hr-green: #50D890;
  --hr-amber: #FFBD66;
  --hr-red: #F27682;
  --hr-violet: #A68DFF;
  min-height: calc(100vh - 56px);
  margin: -24px;
  padding: 22px clamp(16px, 2vw, 30px) 52px;
  overflow: hidden;
  background:
    radial-gradient(circle at 7% -10%, rgba(102, 136, 255, 0.15), transparent 30%),
    radial-gradient(circle at 96% 14%, rgba(166, 141, 255, 0.08), transparent 25%),
    var(--hr-bg);
  color: var(--hr-text);
}
.hr-page * { box-sizing: border-box; }
.hr-hero,
.hr-metrics,
.hr-panel { max-width: 1680px; margin-right: auto; margin-left: auto; }

/* Nagłówek — szybki kontekst zamiast pustej dekoracji. */
.hr-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 22px;
  margin-bottom: 15px;
}
.hr-heading { display: flex; min-width: 0; align-items: center; gap: 14px; }
.hr-heading-icon {
  display: flex;
  width: 50px;
  height: 50px;
  flex: 0 0 50px;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(126, 151, 255, 0.27);
  border-radius: 16px;
  background: linear-gradient(145deg, #4168FF, #765FF1);
  box-shadow: 0 18px 34px -19px rgba(80, 107, 255, 0.9);
}
.hr-heading-icon >>> .v-icon { color: #FFF !important; }
.hr-eyebrow,
.hr-section-eyebrow {
  display: block;
  margin-bottom: 2px;
  color: #8793A9;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.11em;
  text-transform: uppercase;
}
.hr-heading h1 {
  margin: 0;
  color: #FFF;
  font-size: clamp(25px, 2.4vw, 31px);
  font-weight: 900;
  letter-spacing: -0.045em;
  line-height: 1.05;
}
.hr-heading p { max-width: 650px; margin: 6px 0 0; color: var(--hr-muted); font-size: 13.5px; line-height: 1.45; }
.hr-hero-actions { display: flex; flex: 0 0 auto; align-items: stretch; gap: 8px; }
.hr-context-card {
  min-width: 164px;
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  padding: 9px 11px;
  border: 1px solid var(--hr-border);
  border-radius: 13px;
  background: rgba(35, 40, 56, 0.82);
}
.hr-context-card span { display: flex; align-items: center; gap: 5px; color: #BAC3D2; font-size: 12px; font-weight: 700; }
.hr-context-card span >>> .v-icon { color: #7F9AFF !important; }
.hr-context-card strong { grid-row: span 2; color: #FFF; font-size: 19px; font-weight: 850; font-variant-numeric: tabular-nums; }
.hr-context-card small { color: #737F96; font-size: 11px; font-weight: 700; letter-spacing: 0.04em; }
.hr-refresh {
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 0 14px;
  border: 1px solid rgba(126, 151, 255, 0.2);
  border-radius: 12px;
  background: rgba(102, 136, 255, 0.1);
  color: #D8E0FF;
  font: inherit;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
  transition: border-color 0.16s ease, background 0.16s ease, transform 0.16s ease;
}
.hr-refresh >>> .v-icon { color: #8EA7FF !important; }
.hr-refresh:hover:not(:disabled) { border-color: rgba(126, 151, 255, 0.42); background: rgba(102, 136, 255, 0.16); transform: translateY(-1px); }
.hr-refresh:disabled { opacity: 0.62; cursor: wait; }
.is-spinning { animation: hr-spin 0.85s linear infinite; }

/* Cztery liczby, które odpowiadają na bieżące pytania właściciela. */
.hr-metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 9px;
  margin-bottom: 13px;
}
.hr-metric {
  --metric-color: var(--hr-blue);
  position: relative;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 13px 14px;
  overflow: hidden;
  border: 1px solid var(--hr-border);
  border-radius: 15px;
  background: linear-gradient(145deg, rgba(38, 44, 61, 0.97), rgba(31, 36, 52, 0.97));
}
.hr-metric::after {
  position: absolute;
  right: -25px;
  bottom: -39px;
  width: 94px;
  height: 94px;
  border-radius: 50%;
  background: var(--metric-color);
  opacity: 0.055;
  content: "";
}
.hr-metric--amber { --metric-color: var(--hr-amber); }
.hr-metric--green { --metric-color: var(--hr-green); }
.hr-metric--red { --metric-color: var(--hr-red); }
.hr-metric--violet { --metric-color: var(--hr-violet); }
.hr-metric-icon {
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: rgba(102, 136, 255, 0.12);
}
.hr-metric-icon >>> .v-icon { color: var(--metric-color) !important; }
.hr-metric > div { min-width: 0; display: grid; grid-template-columns: 1fr auto; align-items: end; column-gap: 8px; position: relative; z-index: 1; }
.hr-metric small { color: #929DB2; font-size: 12px; font-weight: 750; }
.hr-metric strong { grid-row: span 2; color: #FFF; font-size: 23px; font-weight: 900; letter-spacing: -0.035em; line-height: 1; font-variant-numeric: tabular-nums; }
.hr-metric span:not(.hr-metric-icon) { overflow: hidden; margin-top: 2px; color: #737F96; font-size: 11.5px; text-overflow: ellipsis; white-space: nowrap; }

/* Wspólne powierzchnie sekcji. */
.hr-panel {
  margin-bottom: 13px;
  padding: 17px;
  overflow: hidden;
  border: 1px solid var(--hr-border);
  border-radius: 18px;
  background: rgba(35, 40, 56, 0.96);
  box-shadow: 0 25px 55px -46px rgba(0, 0, 0, 0.95);
}
.hr-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 14px;
}
.hr-section-heading { display: flex; min-width: 0; align-items: center; gap: 11px; }
.hr-section-icon {
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: rgba(102, 136, 255, 0.12);
}
.hr-section-icon >>> .v-icon { color: #83A0FF !important; }
.hr-section-icon--amber { background: rgba(255, 189, 102, 0.1); }
.hr-section-icon--amber >>> .v-icon { color: #FFCA82 !important; }
.hr-section-icon--violet { background: rgba(166, 141, 255, 0.11); }
.hr-section-icon--violet >>> .v-icon { color: #B7A5FF !important; }
.hr-section-heading h2 { margin: 0; color: #F8FAFF; font-size: 16px; font-weight: 850; letter-spacing: -0.02em; line-height: 1.15; }
.hr-section-heading p { margin: 3px 0 0; color: #8490A6; font-size: 12px; line-height: 1.35; }
.hr-section-count {
  min-width: 76px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 7px 10px;
  border: 1px solid var(--hr-border);
  border-radius: 11px;
  background: var(--hr-surface-soft);
  color: #8490A6;
  font-size: 11.5px;
  font-weight: 700;
}
.hr-section-count strong { color: #DCE2EC; font-size: 14px; }
.hr-section-count.has-items { border-color: rgba(255, 189, 102, 0.2); background: rgba(255, 189, 102, 0.08); color: #E6B979; }
.hr-section-count.has-items strong { color: #FFD08F; }
.hr-panel-progress { margin: -1px 0 13px; border-radius: 999px; opacity: 0.76; }
.hr-inline-error {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 13px;
  border: 1px solid rgba(242, 118, 130, 0.22);
  border-radius: 13px;
  background: rgba(242, 118, 130, 0.075);
}
.hr-inline-error > span { width: 34px; height: 34px; flex: 0 0 34px; display: flex; align-items: center; justify-content: center; border-radius: 10px; background: rgba(242, 118, 130, 0.12); }
.hr-inline-error > span >>> .v-icon { color: #FF9AA4 !important; }
.hr-inline-error > div { min-width: 0; flex: 1; }
.hr-inline-error strong { color: #FFE3E6; font-size: 13px; }
.hr-inline-error p { margin: 2px 0 0; color: #C6A2A7; font-size: 12px; }
.hr-inline-error button { padding: 7px 10px; border: 1px solid rgba(242, 118, 130, 0.25); border-radius: 9px; background: rgba(242, 118, 130, 0.1); color: #FFC0C6; font: inherit; font-size: 12px; font-weight: 800; cursor: pointer; }
.hr-empty-state { min-height: 94px; display: flex; align-items: center; justify-content: center; gap: 11px; padding: 18px; border: 1px dashed rgba(255, 255, 255, 0.1); border-radius: 14px; background: rgba(24, 28, 40, 0.43); }
.hr-empty-state > span { width: 43px; height: 43px; flex: 0 0 43px; display: flex; align-items: center; justify-content: center; border-radius: 13px; background: rgba(102, 136, 255, 0.1); }
.hr-empty-state > span >>> .v-icon { color: #8EA7FF !important; }
.hr-empty-state strong { color: #E9EDF5; font-size: 13.5px; }
.hr-empty-state p { margin: 2px 0 0; color: #7F8AA0; font-size: 12px; }
.hr-empty-state--success > span { background: rgba(80, 216, 144, 0.1); }
.hr-empty-state--success > span >>> .v-icon { color: #72E4A5 !important; }

/* Wnioski — pełen kontekst przed kliknięciem decyzji. */
.hr-request-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
.hr-request-card {
  position: relative;
  min-width: 0;
  display: flex;
  flex-direction: column;
  padding: 14px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 15px;
  background: linear-gradient(145deg, #202635, #1D2230);
  transition: border-color 0.16s ease, transform 0.16s ease, box-shadow 0.16s ease;
}
.hr-request-card:hover { border-color: rgba(126, 151, 255, 0.19); transform: translateY(-1px); box-shadow: 0 18px 30px -28px rgba(0, 0, 0, 0.9); }
.hr-request-card.has-conflict { border-color: rgba(242, 118, 130, 0.22); }
.hr-request-accent { position: absolute; top: 12px; bottom: 12px; left: 0; width: 3px; border-radius: 0 3px 3px 0; background: var(--hr-amber); }
.hr-request-card.has-conflict .hr-request-accent { background: var(--hr-red); }
.hr-request-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.hr-person { min-width: 0; display: flex; align-items: center; gap: 9px; }
.hr-avatar,
.hr-cal-avatar {
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(126, 151, 255, 0.17);
  border-radius: 11px;
  background: linear-gradient(145deg, rgba(102, 136, 255, 0.2), rgba(166, 141, 255, 0.12));
  color: #CFD8FF;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.03em;
}
.hr-person > div { min-width: 0; }
.hr-person small { display: block; color: #6F7B92; font-size: 10.5px; font-weight: 750; letter-spacing: 0.04em; text-transform: uppercase; }
.hr-person strong { display: block; overflow: hidden; margin-top: 1px; color: #F7F9FD; font-size: 13px; font-weight: 850; text-overflow: ellipsis; white-space: nowrap; }
.hr-type-badge,
.hr-status-badge {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 5px;
  padding: 5px 8px;
  border-radius: 999px;
  background: rgba(102, 136, 255, 0.11);
  color: #AFC0FF;
  font-size: 11px;
  font-weight: 800;
  line-height: 1.1;
}
.hr-type-badge >>> .v-icon { color: currentColor !important; }
.hr-type-badge.is-amber { background: rgba(255, 189, 102, 0.1); color: #FFD18F; }
.hr-type-badge.is-violet { background: rgba(166, 141, 255, 0.11); color: #C6B9FF; }
.hr-type-badge.is-green { background: rgba(80, 216, 144, 0.1); color: #83E8B0; }
.hr-type-badge.is-neutral { background: rgba(255, 255, 255, 0.055); color: #A7B1C2; }
.hr-request-facts { display: grid; grid-template-columns: 1.35fr 0.65fr; gap: 7px; margin-top: 12px; }
.hr-request-facts > div { min-width: 0; display: grid; grid-template-columns: auto 1fr; padding: 9px 10px; border: 1px solid rgba(255, 255, 255, 0.055); border-radius: 11px; background: rgba(24, 28, 40, 0.62); }
.hr-request-facts > div > span { grid-row: span 2; width: 27px; height: 27px; display: flex; align-items: center; justify-content: center; margin-right: 8px; border-radius: 8px; background: rgba(102, 136, 255, 0.09); }
.hr-request-facts > div > span >>> .v-icon { color: #819CFF !important; }
.hr-request-facts small { color: #717D93; font-size: 10.5px; font-weight: 700; }
.hr-request-facts strong { overflow: hidden; color: #DDE3ED; font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
.hr-request-note { min-height: 38px; display: flex; align-items: flex-start; gap: 7px; margin-top: 7px; padding: 8px 10px; border-radius: 10px; background: rgba(255, 255, 255, 0.028); }
.hr-request-note >>> .v-icon { flex: 0 0 auto; margin-top: 1px; color: #758199 !important; }
.hr-request-note p { display: -webkit-box; margin: 0; overflow: hidden; color: #B8C1D0; font-size: 12px; line-height: 1.45; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.hr-request-note.is-empty p { color: #67738A; font-style: italic; }
.hr-conflict-box { margin-top: 7px; padding: 9px 10px; border: 1px solid rgba(242, 118, 130, 0.18); border-radius: 11px; background: rgba(242, 118, 130, 0.07); }
.hr-conflict-title { display: flex; align-items: center; gap: 6px; color: #FFABB3; }
.hr-conflict-title >>> .v-icon { color: #F68B96 !important; }
.hr-conflict-title strong { font-size: 12px; }
.hr-conflict-title span { min-width: 18px; margin-left: auto; padding: 2px 5px; border-radius: 999px; background: rgba(242, 118, 130, 0.15); color: #FFC1C7; font-size: 10.5px; font-weight: 850; text-align: center; }
.hr-conflict-list { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 7px; }
.hr-conflict-list span { display: inline-flex; gap: 4px; padding: 4px 6px; border-radius: 7px; background: rgba(24, 28, 40, 0.45); color: #A89BA4; font-size: 10.5px; }
.hr-conflict-list b { color: #E8BCC1; }
.hr-request-actions { display: flex; justify-content: flex-end; gap: 7px; margin-top: auto; padding-top: 12px; }
.hr-request-actions >>> .v-btn { min-width: 108px; height: 34px !important; border-radius: 10px; font-size: 11.5px; font-weight: 850; letter-spacing: 0.02em; text-transform: none; }
.hr-request-actions >>> .v-btn--contained.success { background: #2FAE6D !important; box-shadow: 0 10px 22px -16px rgba(80, 216, 144, 0.85) !important; }
.hr-request-actions >>> .v-btn--outlined.error--text { border-color: rgba(242, 118, 130, 0.35) !important; color: #FF9CA6 !important; }

/* Kalendarz desktopowy i agenda dotykowa. */
.hr-section-head--calendar { margin-bottom: 13px; }
.hr-month-control { display: flex; align-items: center; overflow: hidden; border: 1px solid rgba(126, 151, 255, 0.15); border-radius: 12px; background: #1C2130; }
.hr-month-control button { width: 38px; height: 42px; display: inline-flex; align-items: center; justify-content: center; border: 0; background: transparent; color: #96A6C0; cursor: pointer; transition: background 0.16s ease, color 0.16s ease; }
.hr-month-control button:hover { background: rgba(102, 136, 255, 0.1); color: #FFF; }
.hr-month-control button >>> .v-icon { color: currentColor !important; }
.hr-month-control > span { min-width: 148px; padding: 5px 12px; border-right: 1px solid rgba(255, 255, 255, 0.055); border-left: 1px solid rgba(255, 255, 255, 0.055); text-align: center; }
.hr-month-control small { display: block; color: #6F7B92; font-size: 10px; font-weight: 750; letter-spacing: 0.06em; text-transform: uppercase; }
.hr-month-control strong { display: block; margin-top: 1px; color: #E8ECF5; font-size: 12.5px; font-weight: 850; text-transform: capitalize; }
.hr-calendar-layout { display: grid; grid-template-columns: minmax(0, 1fr) 280px; gap: 10px; align-items: stretch; }
.hr-calendar-main { min-width: 0; }
.hr-calendar-scroll { max-height: 490px; overflow: auto; border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 13px; background: #1B202E; scrollbar-color: #3B455C #1B202E; scrollbar-width: thin; }
.hr-calendar-scroll::-webkit-scrollbar { width: 7px; height: 7px; }
.hr-calendar-scroll::-webkit-scrollbar-track { background: #1B202E; }
.hr-calendar-scroll::-webkit-scrollbar-thumb { border-radius: 999px; background: #3B455C; }
.hr-cal { width: 100%; min-width: 930px; border-collapse: separate; border-spacing: 0; table-layout: fixed; }
.hr-cal th,
.hr-cal td { position: relative; width: 26px; min-width: 26px; height: 29px; padding: 0; border-right: 1px solid rgba(255, 255, 255, 0.035); border-bottom: 1px solid rgba(255, 255, 255, 0.04); background: #1F2433; text-align: center; }
.hr-cal thead th { position: sticky; top: 0; z-index: 4; height: 38px; background: #252B3B; }
.hr-cal thead th small { display: block; color: #68748B; font-size: 7px; font-weight: 750; line-height: 1; }
.hr-cal thead th strong { display: block; margin-top: 3px; color: #B6C0D0; font-size: 11.5px; font-weight: 850; line-height: 1; font-variant-numeric: tabular-nums; }
.hr-cal-name { left: 0; z-index: 3; width: 176px !important; min-width: 176px !important; padding: 0 10px !important; background: #222838 !important; text-align: left !important; }
.hr-cal thead .hr-cal-name { z-index: 6; }
.hr-cal thead .hr-cal-name span { display: block; color: #DCE2EC; font-size: 11.5px; font-weight: 850; }
.hr-cal thead .hr-cal-name small { margin-top: 2px; color: #667289; font-size: 7.5px; }
.hr-cal tbody .hr-cal-name { position: sticky; vertical-align: middle; white-space: nowrap; }
.hr-cal tbody .hr-cal-name strong { max-width: 124px; display: inline-block; overflow: hidden; color: #C8D0DE; font-size: 11.5px; font-weight: 750; text-overflow: ellipsis; vertical-align: middle; white-space: nowrap; }
.hr-cal-avatar { width: 23px; height: 23px; margin-right: 7px; border-radius: 7px; font-size: 7px; vertical-align: middle; }
.hr-cal tbody tr:hover td { background-color: #252B3B; }
.hr-cal tbody tr:hover .hr-cal-name { background: #292F40 !important; }
.hr-cal .wknd { background-color: #1B202C; }
.hr-cal .hol { background-color: rgba(242, 118, 130, 0.095); }
.hr-cal .appr::after,
.hr-cal .pend::after { position: absolute; inset: 4px 3px; z-index: 1; border-radius: 4px; content: ""; }
.hr-cal .appr::after { background: rgba(80, 216, 144, 0.8); box-shadow: 0 0 0 1px rgba(112, 236, 165, 0.18); }
.hr-cal .pend::after { background: repeating-linear-gradient(135deg, rgba(255, 189, 102, 0.86) 0 3px, rgba(255, 189, 102, 0.58) 3px 6px); box-shadow: 0 0 0 1px rgba(255, 209, 143, 0.15); }
.hr-cal .is-today { box-shadow: inset 1px 0 #7896FF, inset -1px 0 #7896FF; }
.hr-cal thead .is-today strong { color: #9BB0FF; }
.hr-calendar-mobile { display: none; }
.hr-legend { display: flex; flex-wrap: wrap; gap: 13px; margin: 9px 2px 0; }
.hr-legend span { display: inline-flex; align-items: center; gap: 6px; color: #7F8AA0; font-size: 10.5px; font-weight: 700; }
.hr-legend i { width: 11px; height: 11px; display: inline-block; border-radius: 3px; background: #2A3041; }
.hr-legend i.is-approved { background: rgba(80, 216, 144, 0.8); }
.hr-legend i.is-pending { background: repeating-linear-gradient(135deg, rgba(255, 189, 102, 0.86) 0 3px, rgba(255, 189, 102, 0.58) 3px 6px); }
.hr-legend i.is-weekend { border: 1px solid rgba(255, 255, 255, 0.06); background: #1B202C; }
.hr-legend i.is-holiday { border: 1px solid rgba(242, 118, 130, 0.13); background: rgba(242, 118, 130, 0.095); }
.hr-upcoming { min-width: 0; display: flex; flex-direction: column; overflow: hidden; border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 13px; background: #1B202E; }
.hr-upcoming > header { display: flex; align-items: center; gap: 8px; padding: 10px; border-bottom: 1px solid rgba(255, 255, 255, 0.055); background: #202635; }
.hr-upcoming > header > span { width: 29px; height: 29px; flex: 0 0 29px; display: flex; align-items: center; justify-content: center; border-radius: 9px; background: rgba(102, 136, 255, 0.1); }
.hr-upcoming > header > span >>> .v-icon { color: #83A0FF !important; }
.hr-upcoming > header > div { min-width: 0; flex: 1; }
.hr-upcoming > header small { display: block; color: #6E7A91; font-size: 7.5px; font-weight: 750; letter-spacing: 0.06em; text-transform: uppercase; }
.hr-upcoming > header strong { display: block; overflow: hidden; color: #D8DEE9; font-size: 11.5px; text-overflow: ellipsis; white-space: nowrap; }
.hr-upcoming > header b { min-width: 24px; padding: 4px 6px; border-radius: 999px; background: rgba(102, 136, 255, 0.12); color: #AFC0FF; font-size: 11px; text-align: center; }
.hr-upcoming-list { max-height: 440px; padding: 7px; overflow-y: auto; }
.hr-upcoming-list article { position: relative; padding: 9px 9px 9px 16px; border-radius: 10px; transition: background 0.16s ease; }
.hr-upcoming-list article + article { margin-top: 3px; }
.hr-upcoming-list article:hover { background: rgba(255, 255, 255, 0.03); }
.hr-upcoming-marker { position: absolute; top: 13px; bottom: 13px; left: 6px; width: 2px; border-radius: 999px; background: linear-gradient(var(--hr-blue), var(--hr-violet)); }
.hr-upcoming-top { display: flex; align-items: center; justify-content: space-between; gap: 7px; }
.hr-upcoming-top strong { overflow: hidden; color: #E5E9F1; font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
.hr-upcoming-top small { flex: 0 0 auto; color: #8EA7FF; font-size: 10px; font-weight: 800; }
.hr-upcoming-list p { margin: 3px 0 1px; color: #A0ABBD; font-size: 11px; font-variant-numeric: tabular-nums; }
.hr-upcoming-list article > span:last-child { color: #69758B; font-size: 10px; }
.hr-upcoming-empty { min-height: 170px; display: flex; flex: 1; flex-direction: column; align-items: center; justify-content: center; padding: 20px; color: #68748A; text-align: center; }
.hr-upcoming-empty >>> .v-icon { margin-bottom: 7px; color: #59657A !important; }
.hr-upcoming-empty strong { color: #9AA5B6; font-size: 12px; }
.hr-upcoming-empty span { margin-top: 3px; font-size: 10.5px; }

/* Pule — porównywalne wiersze na desktopie, czytelne karty na telefonie. */
.hr-balance-summary { min-width: 145px; padding: 7px 11px; border: 1px solid rgba(166, 141, 255, 0.17); border-radius: 11px; background: rgba(166, 141, 255, 0.08); text-align: right; }
.hr-balance-summary small { display: block; color: #8D83B7; font-size: 10px; font-weight: 750; text-transform: uppercase; }
.hr-balance-summary strong { display: block; margin-top: 1px; color: #D9D0FF; font-size: 14px; font-weight: 850; font-variant-numeric: tabular-nums; }
.hr-allowance-table { overflow: hidden; border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 13px; background: #1C2130; }
.hr-allowance-head,
.hr-allowance-row { display: grid; grid-template-columns: minmax(210px, 1.15fr) minmax(230px, 1.35fr) minmax(130px, 0.65fr) minmax(190px, 0.78fr); align-items: center; gap: 15px; }
.hr-allowance-head { min-height: 34px; padding: 0 13px; background: #202635; color: #6F7B92; font-size: 10px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; }
.hr-allowance-row { position: relative; min-height: 58px; padding: 8px 13px; border-top: 1px solid rgba(255, 255, 255, 0.045); transition: background 0.15s ease; }
.hr-allowance-row::before { position: absolute; top: 12px; bottom: 12px; left: 0; width: 2px; border-radius: 0 2px 2px 0; background: var(--hr-green); content: ""; }
.hr-allowance-row.is-warning::before { background: var(--hr-amber); }
.hr-allowance-row.is-danger::before { background: var(--hr-red); }
.hr-allowance-row.is-empty::before { background: #667188; }
.hr-allowance-row:hover { background: rgba(255, 255, 255, 0.025); }
.hr-allowance-row .hr-person small { max-width: 230px; overflow: hidden; margin-top: 2px; text-overflow: ellipsis; white-space: nowrap; text-transform: none; }
.hr-pool-progress { min-width: 0; }
.hr-pool-progress > div { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 6px; }
.hr-pool-progress span { color: #8995AA; font-size: 11px; }
.hr-pool-progress b { color: #BEC7D5; font-size: 11px; font-weight: 800; }
.hr-progress-track { height: 6px; display: block; overflow: hidden; border-radius: 999px; background: rgba(255, 255, 255, 0.065); }
.hr-progress-track i { height: 100%; display: block; border-radius: inherit; background: linear-gradient(90deg, #36C77C, #63E6A0); transition: width 0.4s ease; }
.is-warning .hr-progress-track i { background: linear-gradient(90deg, #E6A340, #FFD084); }
.is-danger .hr-progress-track i { background: linear-gradient(90deg, #D85665, #FF8F9A); }
.is-empty .hr-progress-track i { background: #68748A; }
.hr-remaining small { display: block; color: #6DC995; font-size: 10px; font-weight: 750; }
.hr-remaining strong { display: block; margin-top: 2px; color: #E4E9F1; font-size: 12.5px; font-weight: 850; font-variant-numeric: tabular-nums; }
.is-warning .hr-remaining small { color: #E3B66F; }
.is-danger .hr-remaining small { color: #EB818C; }
.is-empty .hr-remaining small { color: #7C879A; }
.hr-allowance-editor { min-width: 0; display: flex; align-items: center; justify-content: flex-end; gap: 7px; }
.hr-allowance-editor label { min-width: 78px; height: 33px; display: flex; align-items: center; overflow: hidden; border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 9px; background: #171B27; transition: border-color 0.15s ease, box-shadow 0.15s ease; }
.hr-allowance-editor label:focus-within { border-color: rgba(126, 151, 255, 0.55); box-shadow: 0 0 0 3px rgba(102, 136, 255, 0.09); }
.hr-allowance-editor input { width: 52px; min-width: 0; height: 100%; padding: 0 5px 0 9px; border: 0; outline: 0; background: transparent; color: #FFF; font: inherit; font-size: 12.5px; font-weight: 800; font-variant-numeric: tabular-nums; }
.hr-allowance-editor input::placeholder { color: #667188; }
.hr-allowance-editor label span { padding-right: 8px; color: #6F7B91; font-size: 10px; font-weight: 700; }
.hr-allowance-editor >>> .v-btn { min-width: 66px; height: 33px !important; border-radius: 9px; font-size: 11px; font-weight: 850; letter-spacing: 0; text-transform: none; }
.hr-allowance-editor >>> .v-btn--disabled { opacity: 0.43; }
.hr-allowance-mobile { display: none; }

.hr-refresh:focus-visible,
.hr-month-control button:focus-visible,
.hr-inline-error button:focus-visible { outline: 2px solid #8EA7FF; outline-offset: 2px; }

@keyframes hr-spin { to { transform: rotate(360deg); } }

@media (max-width: 1200px) {
  .hr-metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .hr-calendar-layout { grid-template-columns: 1fr; }
  .hr-upcoming-list { max-height: none; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 3px; }
  .hr-upcoming-empty { min-height: 120px; }
  .hr-allowance-head,
  .hr-allowance-row { grid-template-columns: minmax(190px, 1.1fr) minmax(200px, 1.2fr) minmax(115px, 0.55fr) minmax(180px, 0.7fr); gap: 10px; }
}

@media (max-width: 920px) {
  .hr-request-grid { grid-template-columns: 1fr; }
  .hr-allowance-desktop { display: none; }
  .hr-allowance-mobile { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
  .hr-pool-card { padding: 12px; border: 1px solid var(--hr-border); border-radius: 13px; background: #1C2130; }
  .hr-pool-card > header { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; }
  .hr-pool-card > header > span { flex: 0 0 auto; padding: 4px 7px; border-radius: 999px; background: rgba(80, 216, 144, 0.09); color: #74DBA2; font-size: 7.5px; font-weight: 800; }
  .hr-pool-card.is-warning > header > span { background: rgba(255, 189, 102, 0.09); color: #E9BD79; }
  .hr-pool-card.is-danger > header > span { background: rgba(242, 118, 130, 0.09); color: #F1959E; }
  .hr-pool-card.is-empty > header > span { background: rgba(255, 255, 255, 0.045); color: #808B9E; }
  .hr-pool-mobile-facts { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 5px; margin: 11px 0 8px; }
  .hr-pool-mobile-facts > div { min-width: 0; padding: 7px; border-radius: 9px; background: rgba(24, 28, 40, 0.6); }
  .hr-pool-mobile-facts small { display: block; overflow: hidden; color: #667289; font-size: 7.5px; text-overflow: ellipsis; white-space: nowrap; }
  .hr-pool-mobile-facts strong { display: block; overflow: hidden; margin-top: 2px; color: #DCE2EC; font-size: 11.5px; text-overflow: ellipsis; white-space: nowrap; }
  .hr-pool-card > .hr-progress-track { margin-bottom: 10px; }
  .hr-pool-card .hr-allowance-editor { justify-content: stretch; }
  .hr-pool-card .hr-allowance-editor label { flex: 0 0 80px; }
  .hr-pool-card .hr-allowance-editor >>> .v-btn { flex: 1; }
}

@media (max-width: 760px) {
  .hr-page { margin: -16px; padding: 16px 10px 38px; }
  .hr-hero { align-items: stretch; flex-direction: column; gap: 12px; }
  .hr-heading { align-items: flex-start; }
  .hr-heading-icon { width: 44px; height: 44px; flex-basis: 44px; border-radius: 14px; }
  .hr-heading h1 { font-size: 25px; }
  .hr-heading p { font-size: 13px; }
  .hr-hero-actions { width: 100%; }
  .hr-context-card { flex: 1; }
  .hr-refresh { flex: 1; }
  .hr-metrics { gap: 7px; }
  .hr-metric { min-height: 72px; gap: 8px; padding: 10px; }
  .hr-metric-icon { width: 32px; height: 32px; flex-basis: 32px; border-radius: 10px; }
  .hr-metric > div { display: flex; flex-direction: column; align-items: flex-start; }
  .hr-metric strong { order: -1; font-size: 21px; }
  .hr-metric small { margin-top: 2px; font-size: 12px; }
  .hr-metric span:not(.hr-metric-icon) { width: 100%; font-size: 11.5px; }
  .hr-panel { padding: 13px; border-radius: 16px; }
  .hr-section-head { align-items: stretch; flex-direction: column; gap: 10px; }
  .hr-section-heading { align-items: flex-start; }
  .hr-section-eyebrow { font-size: 11.5px; }
  .hr-section-heading h2 { font-size: 17px; }
  .hr-section-heading p { font-size: 12.5px; }
  .hr-section-count { align-self: flex-start; }
  .hr-section-head--calendar .hr-month-control { align-self: stretch; justify-content: space-between; }
  .hr-month-control > span { flex: 1; }
  .hr-month-control button { width: 44px; height: 44px; }
  .hr-month-control small { font-size: 11px; }
  .hr-month-control strong { font-size: 13px; }
  .hr-calendar-desktop { display: none; }
  .hr-calendar-mobile { display: grid; gap: 6px; }
  .hr-agenda-card { min-width: 0; display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 8px; padding: 9px; border: 1px solid rgba(255, 255, 255, 0.055); border-radius: 11px; background: #1B202E; }
  .hr-agenda-copy { min-width: 0; display: flex; flex-direction: column; }
  .hr-agenda-copy strong { overflow: hidden; color: #E7EBF3; font-size: 13px; text-overflow: ellipsis; white-space: nowrap; }
  .hr-agenda-copy span { margin-top: 2px; color: #A4AFC0; font-size: 12px; }
  .hr-agenda-copy small { margin-top: 1px; color: #7F8BA0; font-size: 11.5px; }
  .hr-status-badge { padding: 5px 8px; font-size: 11px; }
  .hr-status-badge.is-approved { background: rgba(80, 216, 144, 0.1); color: #7DE7AB; }
  .hr-status-badge.is-pending { background: rgba(255, 189, 102, 0.1); color: #FFD08A; }
  .hr-legend { gap: 8px 11px; }
  .hr-legend span { font-size: 12px; }
  .hr-legend span:nth-child(n+3) { display: none; }
  .hr-upcoming-list { grid-template-columns: 1fr; }
  .hr-upcoming-list article { padding-top: 8px; padding-bottom: 8px; }
  .hr-balance-summary { align-self: flex-start; text-align: left; }
  .hr-person small { font-size: 11.5px; }
  .hr-person strong { font-size: 13px; }
  .hr-type-badge { font-size: 12px; }
  .hr-request-facts small { font-size: 11.5px; }
  .hr-request-facts strong { font-size: 13px; }
  .hr-request-note p { font-size: 12.5px; }
  .hr-conflict-title strong { font-size: 12.5px; }
  .hr-conflict-list span { font-size: 11.5px; }
  .hr-request-actions >>> .v-btn { min-height: 44px; }
  .hr-pool-mobile-facts small { font-size: 11px; }
  .hr-pool-mobile-facts strong { font-size: 12px; }
  .hr-pool-card .hr-allowance-editor label,
  .hr-pool-card .hr-allowance-editor >>> .v-btn { min-height: 42px; }
}

@media (max-width: 560px) {
  .hr-request-top { align-items: stretch; flex-direction: column; }
  .hr-type-badge { align-self: flex-start; }
  .hr-request-facts { grid-template-columns: 1fr; }
  .hr-request-actions >>> .v-btn { min-width: 0; flex: 1; }
  .hr-allowance-mobile { grid-template-columns: 1fr; }
  .hr-inline-error { align-items: flex-start; flex-wrap: wrap; }
  .hr-inline-error button { margin-left: 45px; }
}

@media (max-width: 390px) {
  .hr-page { padding-right: 8px; padding-left: 8px; }
  .hr-heading-icon { display: none; }
  .hr-hero-actions { flex-direction: column; }
  .hr-context-card,
  .hr-refresh { width: 100%; min-height: 43px; }
  .hr-metric { padding: 9px; }
  .hr-metric-icon { display: none; }
  .hr-section-heading p { display: none; }
  .hr-agenda-card { grid-template-columns: auto minmax(0, 1fr); }
  .hr-status-badge { grid-column: 2; justify-self: start; }
}

@media (prefers-reduced-motion: reduce) {
  .hr-refresh,
  .hr-request-card,
  .hr-progress-track i { transition: none; }
  .is-spinning { animation-duration: 1.8s; }
}
</style>

<style>
.hr-swal { border: 1px solid rgba(255, 255, 255, 0.09) !important; border-radius: 18px !important; box-shadow: 0 28px 70px rgba(0, 0, 0, 0.48) !important; }
.hr-swal-input { border: 1px solid rgba(255, 255, 255, 0.12) !important; border-radius: 11px !important; background: #171B27 !important; color: #F5F7FC !important; box-shadow: none !important; }
.hr-swal-input:focus { border-color: #6688FF !important; box-shadow: 0 0 0 3px rgba(102, 136, 255, 0.11) !important; }
.hr-swal-confirm,
.hr-swal-cancel { border-radius: 10px !important; font-size: 13px !important; font-weight: 800 !important; }
</style>
